import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { Search } from "lucide-react";

interface Referral {
  id: string;
  referred_by_id: string;
  referred_to_id: string;
  referral_message: string;
  contact_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  status: string;
  chapter: string;
  created_at: string;
  referral_date: string;
  referred_by_name?: string;
  referred_to_name?: string;
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  contacted: "bg-blue-100 text-blue-800 border-blue-200",
  closed_won: "bg-green-100 text-green-800 border-green-200",
  closed_lost: "bg-red-100 text-red-800 border-red-200",
};

const statusLabels: Record<string, string> = {
  pending: "Pending",
  contacted: "Contacted",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
};

const AdminReferralList = () => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterChapter, setFilterChapter] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    const fetchReferrals = async () => {
      const { data } = await supabase
        .from("referrals")
        .select("*")
        .order("referral_date", { ascending: false })
        .order("created_at", { ascending: false });

      if (!data) {
        setLoading(false);
        return;
      }

      const memberIds = [...new Set(data.flatMap(r => [r.referred_by_id, r.referred_to_id]))];
      const { data: members } = await supabase
        .from("members")
        .select("id, name")
        .in("id", memberIds);

      const memberMap = new Map(members?.map(m => [m.id, m.name]) || []);

      setReferrals(data.map(r => ({
        ...r,
        referred_by_name: memberMap.get(r.referred_by_id) || "Unknown",
        referred_to_name: memberMap.get(r.referred_to_id) || "Unknown",
      })));
      setLoading(false);
    };

    fetchReferrals();
  }, []);

  const filtered = referrals.filter(r => {
    const matchesSearch = searchTerm === "" ||
      r.referred_by_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.referred_to_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.contact_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesChapter = filterChapter === "all" || r.chapter === filterChapter;
    const matchesStatus = filterStatus === "all" || r.status === filterStatus;
    return matchesSearch && matchesChapter && matchesStatus;
  });

  if (loading) return <p className="text-center py-8 text-muted-foreground">Loading referrals...</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by member or contact name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterChapter} onValueChange={setFilterChapter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Chapter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Chapters</SelectItem>
            <SelectItem value="washington">Washington</SelectItem>
            <SelectItem value="union">Union</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="closed_won">Closed Won</SelectItem>
            <SelectItem value="closed_lost">Closed Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Referral Date</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Chapter</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No referrals found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {format(new Date(r.referral_date + "T00:00:00"), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="text-sm font-medium">{r.referred_by_name}</TableCell>
                  <TableCell className="text-sm font-medium">{r.referred_to_name}</TableCell>
                  <TableCell className="text-sm">
                    <div>{r.contact_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.contact_email || r.contact_phone || "—"}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{r.referral_message}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={statusColors[r.status] || ""}>
                      {statusLabels[r.status] || r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm capitalize">{r.chapter}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">
        Showing {filtered.length} of {referrals.length} referrals. Closed amounts are private to recipients.
      </p>
    </div>
  );
};

export default AdminReferralList;
